import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ImmediateDigest from "./Widgets/ImmediateDigest";
import NewsAnchorWidget from "./Widgets/NewsAnchorWidget";
import { getProductFeed } from "../../../api/feed";
import { useArticleSidebar } from "../../Universal/ArticleCardDetailPanel";
import CVEGraph from "../../Universal/CVEGraph";
import { FeedCard } from "../Feeds/FeedCard";

const IS_CP = process.env.REACT_APP_IS_CP === "true";

const getTheme = (dark) =>
  dark
    ? {
        cardBg: "rgba(15,23,42,0.60)",
        cardBorder: "rgba(14,165,233,0.12)",
        textPrimary: "rgba(226,232,240,0.9)",
        textSecondary: "rgba(148,163,184,0.7)",
        textMuted: "rgba(148,163,184,0.4)",
        activeText: "rgba(14,165,233,0.9)",
        activeBorder: "rgba(14,165,233,0.30)",
        activeBg: "rgba(14,165,233,0.15)",
        dangerText: "rgba(248,113,113,0.9)",
      }
    : {
        cardBg: "rgba(241,245,249,0.8)",
        cardBorder: "rgba(203,213,225,0.5)",
        textPrimary: "rgba(15,23,42,0.9)",
        textSecondary: "rgba(71,85,105,0.8)",
        textMuted: "rgba(148,163,184,0.6)",
        activeText: "rgba(2,132,199,1)",
        activeBorder: "rgba(14,165,233,0.30)",
        activeBg: "rgba(14,165,233,0.10)",
        dangerText: "rgba(220,38,38,0.9)",
      };

const panelVariants = {
  hidden: { opacity: 0, filter: "blur(6px)" },
  visible: (i) => ({
    opacity: 1,
    filter: "blur(0px)",
    transition: {
      duration: 0.4,
      delay: i * 0.1,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  }),
};

const PRODUCT_FEED_PAYLOAD = {
  offset: 0,
  group: "public",
  sort: -1,
  target_types: ["ecommerce"]
};

const buildCardEntries = (data) =>
  data.map((article, index) => ({
    id: article.document_id || article.id || `recent-post-${index}`,
    article,
  }));

const EASE_DURATION = 600;

const ScrollColumn = forwardRef(
  (
    { cards, speed, t, isPaused, onCardClick, onCardInteract, activeCardId },
    ref,
  ) => {
    const colRef = useRef(null);
    const offsetRef = useRef(0);
    const rafRef = useRef(null);
    const lastTimeRef = useRef(null);
    const velocityRef = useRef(1);
    const targetRef = useRef(1);

    useImperativeHandle(ref, () => ({
      nudge: (delta) => {
        offsetRef.current += delta;

        const el = colRef.current;
        if (!el) return;

        const scrollH = el.scrollHeight / 2;

        if (scrollH > 0) {
          while (offsetRef.current >= scrollH) offsetRef.current -= scrollH;
          while (offsetRef.current < 0) offsetRef.current += scrollH;
        }

        el.style.transform = `translateY(-${offsetRef.current}px)`;
      },
    }));

    useEffect(() => {
      targetRef.current = isPaused ? 0 : 1;
    }, [isPaused]);

    useEffect(() => {
      const el = colRef.current;
      if (!el) return;

      const tick = (now) => {
        if (lastTimeRef.current == null) lastTimeRef.current = now;

        const dt = now - lastTimeRef.current;
        lastTimeRef.current = now;

        const step = dt / EASE_DURATION;

        if (velocityRef.current < targetRef.current) {
          velocityRef.current = Math.min(velocityRef.current + step, 1);
        } else if (velocityRef.current > targetRef.current) {
          velocityRef.current = Math.max(velocityRef.current - step, 0);
        }

        if (velocityRef.current > 0.001) {
          offsetRef.current += speed * velocityRef.current * (dt / 16.67);
        }

        const scrollH = el.scrollHeight / 2;

        if (scrollH > 0) {
          while (offsetRef.current >= scrollH) offsetRef.current -= scrollH;
          while (offsetRef.current < 0) offsetRef.current += scrollH;
        }

        el.style.transform = `translateY(-${offsetRef.current}px)`;
        rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);

      return () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      };
    }, [speed]);

    return (
      <div className="min-w-0 flex-1 overflow-hidden">
        <div ref={colRef} className="will-change-transform">
          {[...cards, ...cards].map(({ id, article }, i) => (
            <div key={`${id}-${i}`} className="mb-3">
              <FeedCard
                id={id}
                article={article}
                t={t}
                onCardClick={onCardClick}
                onCardInteract={onCardInteract}
                isActive={activeCardId === id}
              />
            </div>
          ))}
        </div>
      </div>
    );
  },
);

const PostWall = ({ dark, t }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [articleCards, setArticleCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const wallRef = useRef(null);
  const columnRef = useRef(null);

  const handleInteract = useCallback((cardId, docId, state) => {
    setArticleCards((prev) =>
      prev.map((entry) =>
        entry.article.document_id === docId || entry.id === cardId
          ? {
              ...entry,
              article: {
                ...entry.article,
                ...state,
              },
            }
          : entry,
      ),
    );
  }, []);

  const { activeCardId, handleCardClick } = useArticleSidebar(
    articleCards,
    handleInteract,
  );

  useEffect(() => {
    const el = wallRef.current;
    if (!el) return;

    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      columnRef.current?.nudge(e.deltaY);
    };

    el.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      el.removeEventListener("wheel", onWheel);
    };
  }, [loading]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await getProductFeed(PRODUCT_FEED_PAYLOAD);
        const data = response?.data ?? response ?? [];

        if (!cancelled) {
          setArticleCards(buildCardEntries(Array.isArray(data) ? data : []));
        }
      } catch (e) {
        console.error("[PostWall] getProductFeed error:", e);

        if (!cancelled) {
          setArticleCards([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <span className="text-[11px]" style={{ color: t.textMuted }}>
          Loading posts…
        </span>
      </div>
    );
  }

  if (articleCards.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <span className="text-[11px]" style={{ color: t.textMuted }}>
          No recent posts
        </span>
      </div>
    );
  }

  const scrollPaused = isPaused || !!activeCardId;

  return (
    <div className="flex h-full w-full flex-col">
      <div
        className="shrink-0 px-3.5 py-2.5"
        style={{
          borderBottom: `1px solid ${
            dark ? "rgba(148,163,184,0.08)" : "rgba(203,213,225,0.3)"
          }`,
        }}
      >
        <span
          className="text-xs tracking-wide"
          style={{ color: t.textPrimary }}
        >
          Recent Posts
        </span>
      </div>

      <div
        ref={wallRef}
        className="relative flex flex-1 overflow-hidden px-2.5"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-10 h-10"
          style={{
            background: dark
              ? "linear-gradient(to bottom, rgba(15,23,42,0.9) 0%, transparent 100%)"
              : "linear-gradient(to bottom, rgba(241,245,249,0.95) 0%, transparent 100%)",
          }}
        />

        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-10"
          style={{
            background: dark
              ? "linear-gradient(to top, rgba(15,23,42,0.9) 0%, transparent 100%)"
              : "linear-gradient(to top, rgba(241,245,249,0.95) 0%, transparent 100%)",
          }}
        />

        <ScrollColumn
          ref={columnRef}
          cards={articleCards}
          speed={0.38}
          t={t}
          isPaused={scrollPaused}
          onCardClick={handleCardClick}
          onCardInteract={handleInteract}
          activeCardId={activeCardId}
        />
      </div>
    </div>
  );
};

const ExploreSkeleton = () => (
  <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-3">
    <div className="mb-3 shrink-0">
      <div
        className="flex flex-wrap gap-1.5 overflow-hidden"
        style={{ maxHeight: 58 }}
      >
        {[16, 12, 14, 20, 13, 16, 12, 18, 14, 11].map((w, i) => (
          <div
            key={i}
            className="animate-pulse rounded-full bg-surfaceBorder"
            style={{ width: `${w * 4 + 20}px`, height: 24 }}
          />
        ))}
      </div>
    </div>

    <div className="mb-3 shrink-0 h-px bg-mainDivider" />

    <span className="shrink-0 text-[10px] font-medium text-velBlue">
      Suggested Questions
    </span>

    <div className="mt-1.5 flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="shrink-0 animate-pulse rounded-lg border border-surfaceBorder bg-surfaceBorder/30"
          style={{ height: 38 }}
        />
      ))}
    </div>
  </div>
);

const SuggestedTopics = ({ topics, questions }) => {
  const isLoading =
    !topics || !questions || (topics.length === 0 && questions.length === 0);
  const navigate = useNavigate();

  const handleTopicClick = useCallback(
    (topic) => {
      navigate(
        `/chat?message=${encodeURIComponent(`Tell me about ${topic}...`)}`,
      );
    },
    [navigate],
  );

  const handleQuestionClick = useCallback(
    (question) => {
      navigate(`/chat?message=${encodeURIComponent(question)}`);
    },
    [navigate],
  );

  return (
    <div className="flex h-full w-full flex-col overflow-hidden font-poppins font-normal">
      <div className="shrink-0 border-b border-mainDivider px-4 py-2.5">
        <span className="text-xs tracking-wide text-mainText">Explore</span>
      </div>

      {isLoading ? (
        <ExploreSkeleton />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-3">
          {topics.length > 0 && (
            <div className="mb-3 shrink-0">
              <div
                className="flex flex-wrap gap-1.5 overflow-hidden"
                style={{ maxHeight: 58 }}
              >
                {topics.map((topic) => (
                  <button
                    key={topic}
                    onClick={() => handleTopicClick(topic)}
                    className="cursor-pointer rounded-full border border-inputBorder bg-transparent px-2.5 py-1 text-[10px] text-mutedText transition-all duration-150 hover:border-velBlue/30 hover:bg-velBlue/10 hover:text-velBlue active:scale-95"
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>
          )}

          {topics.length > 0 && questions.length > 0 && (
            <div className="mb-3 shrink-0 h-px bg-mainDivider" />
          )}

          {questions.length > 0 && (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <span className="shrink-0 text-[10px] font-medium text-velBlue">
                Suggested Questions
              </span>

              <div className="mt-1.5 flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
                {questions.slice(0, 3).map((q) => (
                  <button
                    key={q}
                    onClick={() => handleQuestionClick(q)}
                    className="flex-1 min-h-0 cursor-pointer rounded-lg border border-velBlue/15 bg-velBlue/[0.04] px-3 py-2 text-left text-[11px] leading-snug text-mainText transition-all duration-150 hover:border-velBlue/30 hover:bg-velBlue/10 active:scale-[0.99] flex items-center overflow-hidden"
                  >
                    <span className="line-clamp-3">{q}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const OverviewView = ({ dark }) => {
  const t = useMemo(() => getTheme(dark), [dark]);
  const [hasVideos, setHasVideos] = useState(true);
  const [suggestions, setSuggestions] = useState(null);

  const handleSuggestionsReady = useCallback((data) => {
    setSuggestions((prev) => {
      const hasNew = data?.topics?.length > 0 || data?.questions?.length > 0;
      if (prev && !hasNew) return prev;
      return data;
    });
  }, []);

  return (
    <div className="flex h-full w-full gap-3 p-4 font-poppins font-normal text-mainText">
      <motion.div
        className="min-w-0 flex-1"
        custom={0}
        initial="hidden"
        animate="visible"
        variants={panelVariants}
      >
        <div className="h-full overflow-hidden rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md">
          <ImmediateDigest
            dark={dark}
            t={t}
            onSuggestionsReady={handleSuggestionsReady}
          />
        </div>
      </motion.div>

      <div className="flex flex-1 min-w-0 flex-col gap-3">
        {hasVideos && (
          <motion.div
            className="shrink-0"
            style={{ maxHeight: "33%" }}
            custom={1}
            initial="hidden"
            animate="visible"
            variants={panelVariants}
          >
            <div
              className="h-full rounded-lg overflow-hidden"
              style={{
                background: t.cardBg,
                border: `1px solid ${t.cardBorder}`,
              }}
            >
              <NewsAnchorWidget
                dark={dark}
                t={t}
                onEmpty={() => setHasVideos(false)}
              />
            </div>
          </motion.div>
        )}

        <motion.div
          className="min-h-0"
          style={{ height: "50%" }}
          custom={2}
          initial="hidden"
          animate="visible"
          variants={panelVariants}
        >
          <div className="h-full overflow-hidden rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md">
            <PostWall dark={dark} t={t} />
          </div>
        </motion.div>

        <motion.div
          className="min-h-0"
          style={{ height: "50%" }}
          custom={3}
          initial="hidden"
          animate="visible"
          variants={panelVariants}
        >
          <div className="h-full overflow-hidden rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md">
            {IS_CP ? (
              <CVEGraph dark={dark} t={t} />
            ) : (
              <SuggestedTopics
                topics={suggestions?.topics ?? null}
                questions={suggestions?.questions ?? null}
              />
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OverviewView;
